"use client"

import { useContext, useEffect, useState, useMemo } from "react"
import {
  TypesenseContext,
  SearchFieldCollectionMappings,
  SearchFieldId,
  getTypesenseKey,
  UserContext,
} from "@tc/ui-typesense-client"
import useCloudWatch from "@tc/ui-rum/hooks/useCloudWatch"

interface RegistrationInfo {
  id: string
  registration_name: string
  organisation_name?: string
}

interface RegistrationDocument {
  id: string
  registration_name?: string
  organisation_name?: string
}

/**
 * Fetches registration details from Typesense for given registration IDs.
 * Returns formatted labels: "ID - Organisation Name"
 */
export const useRegistrationDetails = (
  registrationIds: string[],
  context: UserContext = "transfers",
) => {
  const [registrations, setRegistrations] = useState<
    Array<{ id: string; label: string; info: RegistrationInfo | null }>
  >([])
  const [loading, setLoading] = useState(false)

  const { typesenseHost, apiKey, multiCollectionKeysV2 } =
    useContext(TypesenseContext)

  const { logError } = useCloudWatch()

  const idsKey = useMemo(
    () => [...registrationIds].sort().join(","),
    [registrationIds],
  )

  useEffect(() => {
    if (registrationIds.length === 0) {
      setRegistrations([])
      return
    }

    const abortController = new AbortController()

    const fetchRegistrationDetails = async () => {
      setLoading(true)

      const collectionName =
        SearchFieldCollectionMappings[SearchFieldId.Registration]

      const collectionApiKey = getTypesenseKey({
        multiCollectionKeysV2,
        searchApiKey: apiKey,
        collectionName,
        context,
        usePermissions: false,
      })

      if (!collectionApiKey || !typesenseHost) {
        setRegistrations(
          registrationIds.map((id) => ({ id, label: id, info: null })),
        )
        setLoading(false)
        return
      }

      try {
        const filterValues = registrationIds.map((id) => `\`${id}\``).join(",")
        const filterBy = `registration_name:[${filterValues}]`

        const searchParams = new URLSearchParams({
          q: "*",
          query_by: "registration_name",
          filter_by: filterBy,
          per_page: "250",
        })

        const response = await fetch(
          `${typesenseHost}/search/collections/${collectionName}/documents/search?${searchParams.toString()}`,
          {
            headers: {
              "X-Typesense-Api-Key": collectionApiKey,
            },
            signal: abortController.signal,
          },
        )

        if (!response.ok) {
          throw new Error(`Typesense API error: ${response.status}`)
        }

        const data = await response.json()
        const infoMap = new Map<string, RegistrationInfo>()

        if (data.hits) {
          data.hits.forEach((hit: { document: RegistrationDocument }) => {
            const doc = hit.document
            if (doc.registration_name && doc.id) {
              infoMap.set(doc.registration_name, {
                id: doc.id,
                registration_name: doc.registration_name,
                organisation_name: doc.organisation_name,
              })
            }
          })
        }

        const results = registrationIds.map((regName) => {
          const info = infoMap.get(regName) || null
          // Format: "<Consignor ID> - <Consignor organisation name>"
          const label = info
            ? [info.registration_name, info.organisation_name]
                .filter(Boolean)
                .join(" - ")
            : regName
          return { id: regName, label, info }
        })

        setRegistrations(results)
      } catch (error) {
        if ((error as Error).name === "AbortError") return
        logError("Error fetching registration details:", error)
        setRegistrations(
          registrationIds.map((id) => ({ id, label: id, info: null })),
        )
      } finally {
        setLoading(false)
      }
    }

    void fetchRegistrationDetails()

    return () => abortController.abort()
  }, [idsKey, typesenseHost, apiKey, multiCollectionKeysV2, context, logError])

  return { registrations, loading }
}
